import { Contract } from "@/services/backend/rlt/contracts/types/contract";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useState } from "react";
import {
  UpdateLifetimeRequest,
  UpdatePaymentTermsRequest,
  UpdateSpecialAgreementsRequest,
  useRltContractUpdateLifetimeMutation,
  useRltContractUpdatePaymentTermsMutation,
  useRltContractUpdateSpecialAgreementsMutation,
} from "@/services/backend/rlt/contracts/api/contract-api";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { Matcher } from "react-day-picker";
import { Label } from "@/shared/components/ui/label";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/shared/components/ui/calendar";
import { Input } from "@/shared/components/ui/input";
import { Key } from "@/services/backend/rlt/defaults/types/defaults";
import { Contract as ContractDefaults } from "@/services/backend/rlt/defaults/types/contract";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Textarea } from "@/shared/components/ui/textarea";
import { Badge } from "@/shared/components/ui/badge";
import { useRltDefaultsShowQuery } from "@/services/backend/rlt/defaults/api/defaults-api";

export function ContractBaseData({ contract }: { contract: Contract }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex space-x-4">
          <span>{t("Vertrag")}</span>
          <Badge>{t(contract.state)}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <ContractLifetime contract={contract} />
          <PaymentTerms contract={contract} />
          <SpecialAgreements contract={contract} />
        </div>
      </CardContent>
    </Card>
  );
}

function ContractLifetime({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdateLifetimeRequest>({
    id: contract.id,
    lifetime: contract.lifetime,
  });
  const mutation = useRltContractUpdateLifetimeMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    lifetime: contract.lifetime,
  });

  const endDateRestrictions: Matcher = request.lifetime.unlimited
    ? true
    : { before: new Date(request.lifetime.startDate) };

  return (
    <div className="grid grid-cols-2 items-start gap-4">
      <div className="grid items-center gap-1.5">
        <Label>{t("Begin des Vertrages")}</Label>
        <Datepicker
          date={new Date(request.lifetime.startDate)}
          onDateChange={(date) =>
            setRequest({
              ...request,
              lifetime: {
                ...request.lifetime,
                startDate: date.toISOString(),
              },
            })
          }
          disabled={contract.immutable}
        />
        <InputValidationErrors field="lifetime.startDate" error={error} />
      </div>
      <div className="grid items-center gap-1.5">
        <Label>{t("Ende des Vertrages")}</Label>
        <Datepicker
          date={
            request.lifetime.endDate
              ? new Date(request.lifetime.endDate)
              : undefined
          }
          onDateChange={(date) =>
            setRequest({
              ...request,
              lifetime: {
                ...request.lifetime,
                endDate: date.toISOString(),
              },
            })
          }
          disabled={endDateRestrictions}
        />
        <InputValidationErrors field="liftime.endDate" error={error} />
        <div className="flex items-center gap-1.5">
          <Checkbox
            checked={request.lifetime.unlimited}
            onCheckedChange={(checked) => {
              const unlimited = Boolean(checked);
              if (typeof unlimited === "undefined") return;

              setRequest({
                ...request,
                lifetime: {
                  ...request.lifetime,
                  endDate: null,
                  unlimited,
                },
              });
            }}
            disabled={contract.immutable}
          />
          <Label>{t("Unbegrenzte Laufzeit")}</Label>
          <InputValidationErrors field="lifetime.unlimited" error={error} />
        </div>
      </div>
    </div>
  );
}

function Datepicker({
  date,
  onDateChange,
  disabled = undefined,
}: {
  // undefined is used since the Calendar component's
  // interface uses undefined
  date: Date | undefined;
  onDateChange: (date: Date) => void;
  disabled?: Matcher | Matcher[] | undefined;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => onDateChange(d ?? new Date())}
          disabled={disabled}
          captionLayout="dropdown"
        />
      </PopoverContent>
    </Popover>
  );
}

function PaymentTerms({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdatePaymentTermsRequest>({
    id: contract.id,
    terms: contract.paymentTerms,
  });
  const mutation = useRltContractUpdatePaymentTermsMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    terms: contract.paymentTerms,
  });

  return (
    <div className="grid items-center gap-1.5">
      <Label>
        {t("Zahlungsbedingungen (Formulierung für Paragraph 5 im Vertrag)")}
      </Label>
      <div className="flex w-full items-center space-x-4">
        <Input
          value={request.terms}
          onChange={(e) => setRequest({ ...request, terms: e.target.value })}
          disabled={contract.immutable}
        />
        <PaymentTermsPicker
          onSelect={(terms) => setRequest({ ...request, terms })}
          disabled={contract.immutable}
        />
      </div>
      <InputValidationErrors field="terms" error={error} />
    </div>
  );
}

function PaymentTermsPicker({
  onSelect,
  disabled = undefined,
}: {
  onSelect: (term: string) => void;
  disabled?: boolean;
}) {
  const { data, error } = useRltDefaultsShowQuery({
    key: Key.Contract,
  });

  const defaultsData = data?.data as ContractDefaults;
  const paymentTerms = defaultsData?.paymentTerms ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={disabled}>
          <span>{t("Aus Standard übernehmen")}</span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <RTKQueryErrorAlert error={error} />
        <DropdownMenuGroup>
          {paymentTerms.map((term) => (
            <DropdownMenuItem key={term} onSelect={() => onSelect(term)}>
              {term}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SpecialAgreements({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdateSpecialAgreementsRequest>({
    id: contract.id,
    agreements: contract.specialAgreements,
  });
  const mutation = useRltContractUpdateSpecialAgreementsMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    agreements: contract.specialAgreements,
  });

  return (
    <div className="grid items-center gap-1.5">
      <Label>
        {t(
          "Besondere Vereinbarungen (OPTIONAL: Formulierung für Paragraph 8 im Vertrag)",
        )}
      </Label>
      <Textarea
        value={request.agreements}
        onChange={(e) => setRequest({ ...request, agreements: e.target.value })}
        disabled={contract.immutable}
      />
      <InputValidationErrors field="agreements" error={error} />
    </div>
  );
}
